import { useMemo, useState } from 'react';
import { boundLicenseStore } from '../../state/boundLicense';
import { licenceBlockFor } from '../../state/licenceGate';
import { LicenceSheet } from '../inspections/LicenceSheet';
import { goBack, navigate, resetNav } from '../../app/navigation';
import { agreementAllowsCloudStorage, agreementSigned } from '../../domain/agreement';
import { displayAddress, isInspectionLocked } from '../../domain/inspection';
import { REPORT_VARIANT_ORDER, type ReportFileVariant, type ReportOutputLanguage, type ReportVariant, type V2ReportRecord } from '../../domain/report';
import { useT, type MessageKey } from '../../i18n/i18n';
import { lockBannerFor, reportDueMessage } from '../../i18n/labels';
import { nyReportDue } from '../../domain/reportDue';
import { assembleReport, type ReportModel } from '../../report/assembly';
import { brandingForPrint } from '../../state/reportBranding';
import { jsPdfRenderer, type PdfRenderer } from '../../report/pdf';
import { renderVariantHtml } from '../../report/renderHtml';
import { shareReportFile } from '../../report/share';
import { buildCloseout, rowFactsFrom } from '../../state/closeout';
import { aiStore } from '../../state/aiStore';
import { aiJobsStore } from '../../state/aiJobsStore';
import { advancedSummariesOf } from '../../state/rowFacts';
import { useStore } from '../../state/createStore';
import { evidenceStore } from '../../state/evidenceStore';
import { findingStore } from '../../state/findingStore';
import { formalWorkflowBlocked } from '../../domain/inspectionContext';
import { formalWorkflowGateFor } from '../../state/formalWorkflow';
import { reportReadinessOf } from '../../state/reportReadiness';
import { agreementPdfFailed, agreementPdfReady, agreementPdfStore, retryAgreementPdf } from '../../state/agreementPdfStore';
import { ReadinessList } from './ReadinessList';
import { inspectionStore, setClientChineseSummaryAction, setReportLanguageAction } from '../../state/inspectionStore';
import { FormalWorkflowGateCard } from '../inspections/FormalWorkflowGateCard';
import { generateFinalReportAction, loadReportFileAction, markReportPreviewed, reportPreviewStatusOf, reportPreviewStore, reportsOfInspection, reportStore } from '../../state/reportStore';
import { AgreementViewSheet } from '../inspections/AgreementViewSheet';
import { AddendumSheet } from './AddendumSheet';
import { REPORT_FILE_LABEL, reportFileIcon } from './variantLabels';
import { CloudNote } from './CloudNote';
import { reportCloudStore } from '../../state/reportCloudStore';
import { Banner, Button, Card, ConfirmSheet, EmptyState, InlineMessage, ListRow, SectionHeader, SegmentedControl, SheetShell, SwitchRow, TopBar, showToast } from '../../ui/components';
import { useStickyFooterClearance } from '../../ui/stickyFooter';
import { formatDate } from '../../ui/hooks';

// Report (task book §18): the Report Language is chosen ONLY here, at the end — English → one English
// PDF; Chinese → two PDFs (English client copy + Chinese inspector copy). Generating the final report
// locks the working data (the confirmation says exactly that); every generated PDF is kept as a record
// and can be shared / saved. Preview renders the same assembly the PDF uses.
const VARIANT_LABEL: Record<ReportFileVariant, MessageKey> = REPORT_FILE_LABEL;

// The PDF renderer is injectable for tests / development scenarios (jsPDF + html2canvas in the app).
let rendererOverride: PdfRenderer | null = null;
export function setPdfRendererForTests(renderer: PdfRenderer | null): void {
  rendererOverride = renderer;
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export function ReportScreen(props: { inspectionId: string }) {
  const { t, tp } = useT();
  const footerRef = useStickyFooterClearance();
  const inspectionState = useStore(inspectionStore, (s) => s);
  const findingState = useStore(findingStore, (s) => s);
  const evidenceState = useStore(evidenceStore, (s) => s);
  const reportState = useStore(reportStore, (s) => s);
  const previewState = useStore(reportPreviewStore, (s) => s);
  const agreementPdfState = useStore(agreementPdfStore, (s) => s);
  const inspection =inspectionState.inspections.find((i) => i.id === props.inspectionId) ?? null;
  const aiState = useStore(aiStore, (s) => s);
  const jobs = useStore(aiJobsStore, (s) => s.jobs);
  const advancedSummaries = useMemo(() => advancedSummariesOf(jobs), [jobs]);
  const closeout = useMemo(() => buildCloseout(props.inspectionId, inspectionState, findingState, rowFactsFrom(aiState, evidenceState, advancedSummaries)), [props.inspectionId, inspectionState, findingState, aiState, evidenceState, advancedSummaries]);
  const records = useMemo(() => reportsOfInspection(reportState, props.inspectionId), [reportState, props.inspectionId]);
  // NY F1 Round 2 (OC-2): READINESS — the second gate, read from the one canonical evaluator.
  const readiness = useMemo(() => reportReadinessOf(props.inspectionId, inspectionState), [props.inspectionId, inspectionState]);
  // OD-12 §5.4: New York's five-business-day delivery reminder belongs at the top of the Closeout
  // AND of the report status, in the approved wording. It reminds — it never blocks generating or
  // sharing, and it disappears once the report is generated.
  const due = useMemo(
    () => (inspection ? nyReportDue(inspection, inspectionState.areas.filter((a) => a.inspectionId === props.inspectionId), Date.now()) : null),
    [inspection, inspectionState.areas, props.inspectionId]
  );
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState<'generate' | 'share' | 'preview' | null>(null);
  // The licence gate re-reads when the bound licence changes (binding here, or the monthly re-check).
  const boundLicense = useStore(boundLicenseStore, (s) => s.license);
  const licenceClock = useMemo(() => Date.now(), [boundLicense]);
  const [licenceSheetOpen, setLicenceSheetOpen] = useState(false);
  const [message, setMessage] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);
  const [preview, setPreview] = useState<{ variant: ReportVariant; model: ReportModel } | null>(null);
  const [agreementOpen, setAgreementOpen] = useState(false);
  const [addendumOpen, setAddendumOpen] = useState(false);
  // 2026-10-19 (Owner item 25): whether each delivered PDF is already in the account or still queued.
  const cloud = useStore(reportCloudStore, (s) => s);

  if (!inspection) {
    return (
      <div className="screen">
        <TopBar title={t('report.title')} onBack={() => resetNav({ screen: 'reports' })} />
        <EmptyState title={t('inspection.notFoundTitle')} body={t('inspection.notFoundBody')} />
      </div>
    );
  }

  const locked = isInspectionLocked(inspection);
  // OD-12: a generation now also deletes the working data, and the banner says which of the two
  // states this inspection is in (src/i18n/labels.ts lockBannerFor).
  const lockBanner = lockBannerFor(t, inspection);
  const language: ReportOutputLanguage = inspection.reportLanguage === 'zh-CN' ? 'zh-CN' : 'en-US';
  const signed = agreementSigned(inspection.agreement);
  // NY F1 Round 5 (OD-12 / approved draft §5.1): Generate stays disabled until every output has been
  // opened in the preview, as it stands now. The store refuses regardless (reason 'notPreviewed');
  // this keeps the button honest instead of letting it fail.
  const previews = reportPreviewStatusOf(props.inspectionId, language, previewState);
  const variants = previews.outputs.map((o) => o.variant);
  // Android acceptance 2026-10-21: generating deletes the working data, the agreement's signatures among it,
  // so the planned list lost its agreement row and a preview rendered an empty, unsigned report. Once
  // generated, the list names the PDFs that were made and nothing is previewed again (the files are below).
  const generatedVariants = REPORT_VARIANT_ORDER.filter((variant) => records.some((r) => r.variant === variant));
  const demo = inspection.agreement.demo;
  const agreementFailed = signed && agreementPdfFailed(inspection, agreementPdfState);
  // NY F1 Round 1 (§15.1 effect 2 / §7.3). ELIGIBILITY, not readiness: this is not OC-2's Report
  // Readiness block, which is Round 2. The store refuses regardless — this only keeps the
  // button honest instead of letting it fail.
  const gate = formalWorkflowGateFor(inspection);
  const gateBlocked = formalWorkflowBlocked(gate);
  // 2026-10-19 (Owner items 11 / 50): the account's bound, current licence (re-read when it changes).
  const licence = licenceBlockFor(inspection, licenceClock);
  const canGenerate = !locked && !gateBlocked && licence === null && closeout.canFinalize && readiness.ready && previews.allPreviewed && busy === null;

  async function chooseLanguage(next: ReportOutputLanguage) {
    if (locked) return;
    await setReportLanguageAction(props.inspectionId, next);
  }

  async function chooseClientSummary(on: boolean) {
    if (locked) return;
    await setClientChineseSummaryAction(props.inspectionId, on);
  }

  async function generate() {
    setConfirmOpen(false);
    setBusy('generate');
    setMessage(null);
    try {
      const outcome = await generateFinalReportAction(props.inspectionId, language, { renderer: rendererOverride ?? jsPdfRenderer });
      if (outcome.ok) {
        showToast(tp('report.generatedToast', outcome.records.length));
        setMessage({ kind: 'ok', text: tp('report.generatedToast', outcome.records.length) });
      } else {
        const text =
          outcome.reason === 'blockedJurisdiction' || outcome.reason === 'blockedLegalReview'
            ? t('gate.actionUnavailable')
            : outcome.reason === 'licenceMissing' || outcome.reason === 'licenceExpired'
            ? t(outcome.reason === 'licenceMissing' ? 'report.licenceMissing' : 'report.licenceExpired')
            : outcome.reason === 'blocked'
            ? t('closeout.openWithGaps', { n: Number(outcome.detail) })
            : outcome.reason === 'notReady'
              ? tp('readiness.blocked', Number(outcome.detail))
            : outcome.reason === 'notPreviewed'
              ? tp('report.previewPending', Number(outcome.detail))
            : outcome.reason === 'locked'
              ? t('report.lockedTitle')
              : outcome.reason === 'notFound'
                ? t('inspection.notFoundTitle')
                : t('report.generateFailed');
        // iPhone real-photo run 2026-10-26: the inspector reads a sentence in their language; the engine's
        // own words ("The operation is insecure.") stay in the log for us.
        if (outcome.reason === 'renderFailed') console.warn('[report] generation failed:', outcome.detail);
        setMessage({ kind: 'err', text });
      }
    } finally {
      setBusy(null);
    }
  }

  async function share(record: V2ReportRecord) {
    setBusy('share');
    setMessage(null);
    try {
      const blob = await loadReportFileAction(record.id);
      if (!blob) {
        setMessage({ kind: 'err', text: t('report.fileMissing') });
        return;
      }
      const outcome = await shareReportFile(record, blob);
      if (outcome.code === 'failed') setMessage({ kind: 'err', text: t('report.shareFailed', { detail: outcome.detail }) });
      else if (outcome.code === 'downloaded') setMessage({ kind: 'ok', text: t('report.downloaded', { name: record.fileName }) });
    } finally {
      setBusy(null);
    }
  }

  // In the account (the list from reports-v2 has it) · waiting to be copied · nothing known yet.
  function cloudLabel(reportId: string): string {
    if (cloud.files.some((f) => f.reportId === reportId)) return t('cloud.saved');
    if (cloud.pending.includes(reportId)) return t('cloud.pending');
    // v4 consent clause (2): without the client's consent the PDFs stay on this device only (2026-10-20).
    if (inspection && !agreementAllowsCloudStorage(inspection.agreement)) return t('cloud.deviceOnly');
    return '';
  }

  async function openPreview(variant: ReportVariant) {
    if (variant === 'agreement') {
      setAgreementOpen(true);
      markReportPreviewed(props.inspectionId, variant);
      return;
    }
    setBusy('preview');
    try {
      // the preview carries the same branding the PDF will (Owner item 18)
      const branding = await brandingForPrint();
      const model = await assembleReport(props.inspectionId, variant, { inspection: inspectionState, findings: findingState, evidence: evidenceState, ai: aiState, advancedSummaries, generatedAt: Date.now(), branding, severityLabels: branding.severityLabels });
      setPreview({ variant, model });
      markReportPreviewed(props.inspectionId, variant);
    } catch (raw) {
      console.warn('[report] preview failed:', raw);
      setMessage({ kind: 'err', text: t('report.previewFailed') });
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="screen" data-testid="report-screen">
      <TopBar title={t('report.title')} subtitle={displayAddress(inspection, t('inspection.noAddress'))} onBack={goBack} />
      <div className="screen-body">
        {due ? (
          <p className={`closeout-due ${due.overdue ? 'closeout-due-overdue' : ''}`} data-testid="report-due">
            {reportDueMessage(t, due, formatDate(due.dueAt))}
          </p>
        ) : null}
        {locked ? (
          <div className="banner-slot">
            <Banner tone="success" icon="lock" title={lockBanner.title} body={lockBanner.body ?? undefined} testId="report-locked" />
          </div>
        ) : gateBlocked ? (
          <FormalWorkflowGateCard inspection={inspection} gate={gate} />
        ) : (
          <Card testId="report-status">
            <SectionHeader title={t('report.statusTitle')} />
            {closeout.canFinalize ? (
              // Issue 11: 「没有待补项」 right above 「还差 N 项描述项」 read as a contradiction.
              <p className="card-body" data-testid="report-status-ready">
                {readiness.applies && !readiness.ready ? t('report.statusCloseoutClear') : t('closeout.readyTitle')}
              </p>
            ) : (
              <>
                <p className="card-body" data-testid="report-status-gaps">
                  {t('closeout.openWithGaps', { n: closeout.blocking.length })}
                </p>
                <div className="card-actions">
                  <Button variant="secondary" icon="task_alt" onClick={() => navigate({ screen: 'closeout', inspectionId: props.inspectionId })} testId="report-go-closeout">
                    {t('closeout.title')}
                  </Button>
                </div>
              </>
            )}
            {readiness.applies ? (
              readiness.ready ? (
                <p className="card-note" data-testid="report-readiness-ready">
                  {t('readiness.ready')}
                </p>
              ) : (
                <ReadinessList inspectionId={props.inspectionId} readiness={readiness} state={inspectionState} testId="report-readiness" />
              )
            ) : (
              <p className="card-note" data-testid="report-readiness-na">
                {t('readiness.notApplicable')}
              </p>
            )}
            {licence ? (
              // Owner items 11 / 50: a block, with the way to fix it right here.
              <div className="report-licence-block" data-testid="report-licence-block" data-reason={licence}>
                <InlineMessage kind="err" text={t(licence === 'licenceMissing' ? 'report.licenceMissing' : 'report.licenceExpired')} />
                <Button variant="secondary" icon="verified_user" onClick={() => (licence === 'licenceMissing' ? setLicenceSheetOpen(true) : navigate({ screen: 'settingsPage', page: 'profile' }))} testId="report-licence-fix">
                  {licence === 'licenceMissing' ? t('licence.bind') : t('licence.recheck')}
                </Button>
              </div>
            ) : null}
          </Card>
        )}

        <Card testId="report-language-card">
          <SectionHeader title={t('shell.reportLanguage')} meta={t('report.languageBody')} />
          <SegmentedControl
            variant="pill"
            block
            ariaLabel={t('shell.reportLanguage')}
            value={language}
            onChange={(next) => void chooseLanguage(next)}
            options={[
              { key: 'en-US', label: t('language.english'), testId: 'report-lang-en', disabled: locked },
              { key: 'zh-CN', label: t('language.chinese'), testId: 'report-lang-zh', disabled: locked }
            ]}
            testId="report-language"
          />
          {/* Owner decision list 2026-10-19, item 23: a separate Chinese summary PDF for the client. */}
          <SwitchRow label={t('report.clientSummaryToggle')} on={inspection.clientChineseSummary} onToggle={(on) => void chooseClientSummary(on)} disabled={locked} testId="report-client-summary" />
          <p className="card-note">{t('report.clientSummaryToggleHint')}</p>
          {/* Device walkthrough 2026-10-27 B1: the preview buttons sat at the bottom of this card, under the agreement
              and DEMO notes — on a 430×932 iPhone right where the sticky 「生成最终报告」 bar starts, so the very button the
              bar asks for (「还有 1 份报告未预览」) was half under it whenever the page was opened. TestFlight 210
              (2026-10-28): placed after the list of PDFs, 「View agreement」 was still ~45 pt under the bar on the English
              iPhone screen while a preview was pending, so they now come right after the language choice and the list
              of PDFs with its preview note follows them. (The bar is sticky, in the flow: scrolled to the end nothing
              is under it.) */}
          {!locked ? (
            <div className="card-actions" data-testid="report-preview-actions">
              {variants.map((variant) => (
                <Button key={variant} variant="secondary" icon="visibility" onClick={() => void openPreview(variant)} disabled={busy !== null} testId={`report-preview-${variant}`}>
                  {variant === 'agreement' ? t('agreement.view') : t('report.preview', { name: t(VARIANT_LABEL[variant]) })}
                </Button>
              ))}
            </div>
          ) : null}
          <ul className="subtle-list report-outputs" data-testid="report-outputs">
            {locked
              ? generatedVariants.map((variant) => (
                  <li key={variant} data-variant={variant} data-generated="true">
                    {t(VARIANT_LABEL[variant])}
                    {demo ? ` · ${t('report.demoWatermark')}` : ''}
                    {` · ${t('report.outputGenerated')}`}
                  </li>
                ))
              : previews.outputs.map((output) => {
                  // Owner 2026-10-21: the agreement is made when both parties sign and needs no preview.
                  const prepared = output.variant === 'agreement' && agreementPdfReady(inspection, agreementPdfState);
                  // iPhone real-photo run 2026-10-26: a failed one used to read 「不用预览」 like a fine one.
                  const agreementState: MessageKey = prepared
                    ? 'report.agreementPrepared'
                    : agreementFailed
                      ? 'report.agreementFailed'
                      : agreementPdfState.drawing === inspection.id
                        ? 'report.agreementDrawing'
                        : 'report.agreementNoPreview';
                  return (
                    <li key={output.variant} data-variant={output.variant} data-previewed={output.previewed ? 'true' : 'false'} data-prepared={prepared ? 'true' : undefined} data-failed={output.variant === 'agreement' && agreementFailed ? 'true' : undefined}>
                      {t(VARIANT_LABEL[output.variant])}
                      {demo ? ` · ${t('report.demoWatermark')}` : ''}
                      {!output.needsPreview ? ` · ${t(agreementState)}` : output.previewed ? ` · ${t('report.previewed')}` : ''}
                    </li>
                  );
                })}
          </ul>
          {!locked && !previews.allPreviewed ? <p className="card-note" data-testid="report-preview-gate">{t('report.previewFirst')}</p> : null}
          {!locked && agreementFailed ? (
            <Banner
              tone="warning"
              icon="warning"
              title={t('report.agreementFailed')}
              body={t('agreement.pdfKeptFailed')}
              action={
                <Button variant="text" size="small" block={false} icon="refresh" onClick={() => retryAgreementPdf(inspection.id)} testId="report-agreement-retry">
                  {t('common.retry')}
                </Button>
              }
              testId="report-agreement-failed"
            />
          ) : null}
          {/* Android acceptance 2026-10-21 (issue 14): once the report is generated the working copy of the
              signatures is gone, so 「签署后可单独导出」 showed next to an agreement PDF that already existed. */}
          <p className="card-note" data-testid="report-agreement-note">
            {signed || locked ? t('report.agreementSeparate') : t('report.agreementUnsignedNote')}
          </p>
          {demo ? <Banner tone="warning" icon="warning" title={t('report.demoBanner')} body={t('agreement.demoHint')} testId="report-demo-banner" /> : null}
        </Card>

        {message ? <InlineMessage kind={message.kind} text={message.text} /> : null}

        {locked ? (
          // Owner decision list 2026-10-19 item 27: the delivered report is never changed; an addendum corrects it.
          <Card testId="report-addendum-card">
            <SectionHeader title={t('addendum.cardTitle')} />
            <p className="card-body">{t('addendum.cardBody')}</p>
            <div className="card-actions">
              <Button variant="secondary" icon="edit_note" onClick={() => setAddendumOpen(true)} testId="report-addendum-write">
                {t('addendum.write')}
              </Button>
            </div>
          </Card>
        ) : null}

        <div className="list" data-testid="report-files">
          <SectionHeader title={t('report.filesTitle')} meta={records.length > 0 ? tp('report.filesCount', records.length) : t('report.filesEmpty')} />
          {records.map((record) => (
            <ListRow
              key={record.id}
              icon={reportFileIcon(record.variant)}
              title={record.fileName}
              subtitle={[t(VARIANT_LABEL[record.variant]), tp('report.pages', record.pageCount), formatBytes(record.byteSize), formatDate(record.generatedAt), cloudLabel(record.id)].filter((part) => part.length > 0).join(' · ')}
              trailing={<span className="badge badge-muted">{t('report.share')}</span>}
              onClick={() => void share(record)}
              dataName={record.fileName}
              testId="report-file-row"
            />
          ))}
          {records.some((r) => cloud.pending.includes(r.id)) ? <CloudNote state={cloud.state} /> : null}
        </div>
      </div>

      {!locked ? (
        <div ref={footerRef} className="sticky-actions" data-testid="report-actions">
          {!closeout.canFinalize ? <p className="blockers-note">{t('closeout.openWithGaps', { n: closeout.blocking.length })}</p> : null}
          {closeout.canFinalize && !readiness.ready ? <p className="blockers-note" data-testid="report-readiness-note">{tp('readiness.blocked', readiness.blockers.length)}</p> : null}
          {closeout.canFinalize && readiness.ready && !previews.allPreviewed ? <p className="blockers-note" data-testid="report-preview-note">{tp('report.previewPending', previews.pending.length)}</p> : null}
          <Button icon="picture_as_pdf" onClick={() => setConfirmOpen(true)} disabled={!canGenerate} sub={tp('report.generateSub', variants.length)} testId="report-generate">
            {busy === 'generate' ? t('report.generating') : t('report.generate')}
          </Button>
        </div>
      ) : null}

      {confirmOpen ? (
        // OD-12 / approved draft §5.2: the strict confirmation states exactly what generation does —
        // final, unchangeable, working data deleted, five business days. The checkbox form of this
        // sheet is the redesigned screen's; the wording is the approved one either way.
        <ConfirmSheet title={t('report.finalConfirmTitle')} body={t('report.finalConfirmBody')} acknowledge={t('report.finalConfirmCheck')} confirmLabel={t('report.finalConfirmAction')} onConfirm={() => void generate()} onCancel={() => setConfirmOpen(false)} />
      ) : null}

      {preview ? (
        <SheetShell full title={t(VARIANT_LABEL[preview.variant])} subtitle={t('report.previewSubtitle')} onClose={() => setPreview(null)} testId="report-preview-sheet">
          <div className="report-preview" data-testid="report-preview" dangerouslySetInnerHTML={{ __html: renderVariantHtml(preview.variant, preview.model) }} />
        </SheetShell>
      ) : null}
      {licenceSheetOpen ? <LicenceSheet onSaved={() => setLicenceSheetOpen(false)} onClose={() => setLicenceSheetOpen(false)} /> : null}
      {addendumOpen ? <AddendumSheet inspectionId={props.inspectionId} renderer={rendererOverride} onClose={() => setAddendumOpen(false)} onCreated={() => setAddendumOpen(false)} /> : null}
      {agreementOpen ? <AgreementViewSheet agreement={inspection.agreement} subject={inspection} onClose={() => setAgreementOpen(false)} /> : null}
    </div>
  );
}
